import { NewTask, RoleOption } from '@griply/types';

import { newTaskConverter } from '../converters';
import { dateToFirestoreTimestamp } from '../converters/date-to-firestore-timestamp';
import { FirestoreInstance } from '../types/firestore';
import { FirestoreCollections } from '../types/firestore-collections';

export type CreateTasksOptions<
  TDocRef = unknown,
  TCollectionRef = unknown,
  TFieldValue = unknown,
  TTimestamp = unknown,
  TDb = FirestoreInstance,
> = {
  tasks: NewTask[];
  userId: string;
  collection: (
    db: NoInfer<TDb>,
    path: string,
    ...rest: string[]
  ) => TCollectionRef;
  doc: (
    collectionRef: NoInfer<TCollectionRef>,
    ...rest: string[]
  ) => TDocRef & { id: string };
  setDoc: (
    ref: NoInfer<TDocRef>,
    data: Record<string, unknown>,
  ) => Promise<void> | void;
  db: TDb;
  serverTimestamp: () => TFieldValue;
  timestampFromDate: (date: Date) => TTimestamp;
  writeBatch?: (db: NoInfer<TDb>) => {
    set: (ref: NoInfer<TDocRef>, data: Record<string, unknown>) => void;
    commit: () => Promise<void>;
  };
};

const buildTaskDocument = <TDocRef, TFieldValue, TTimestamp>(
  task: NewTask,
  docRef: TDocRef & { id: string },
  userId: string,
  serverTimestamp: () => TFieldValue,
  timestampFromDate: (date: Date) => TTimestamp,
): Record<string, unknown> => {
  const converted = newTaskConverter(task);
  const firestoreData = dateToFirestoreTimestamp(converted, timestampFromDate);

  return {
    ...(firestoreData as Record<string, unknown>),
    id: docRef.id,
    updatedAt: serverTimestamp(),
    parentIds: null,
    roles: { [RoleOption.Owner]: userId, [RoleOption.All]: [userId] },
  };
};

export const createTasks = async <
  TDocRef,
  TCollectionRef,
  TFieldValue,
  TTimestamp,
  TDb = FirestoreInstance,
>(
  options: CreateTasksOptions<
    TDocRef,
    TCollectionRef,
    TFieldValue,
    TTimestamp,
    TDb
  >,
): Promise<void> => {
  const {
    tasks,
    userId,
    collection,
    doc,
    setDoc,
    db,
    serverTimestamp,
    timestampFromDate,
    writeBatch,
  } = options;

  const taskCollection = collection(db, FirestoreCollections.Tasks);

  if (tasks.length === 1 || !writeBatch) {
    const promises = tasks.map((task) => {
      const docRef = doc(taskCollection);
      const data = buildTaskDocument(
        task,
        docRef,
        userId,
        serverTimestamp,
        timestampFromDate,
      );
      return setDoc(docRef, data);
    });
    await Promise.all(promises);
    return;
  }

  const batch = writeBatch(db);
  tasks.forEach((task) => {
    const docRef = doc(taskCollection);
    const data = buildTaskDocument(
      task,
      docRef,
      userId,
      serverTimestamp,
      timestampFromDate,
    );
    batch.set(docRef, data);
  });
  await batch.commit();
};
